import { Permission } from "../../../types/permissions";

/**
 * Mirrors the exact hierarchy Layout.tsx's `menuItems` builds for the app's
 * sidebar — same sections, same order, same permission each item is gated
 * by. Leaves are sourced from `SCREEN_PERMISSION` (types/screenPermissions.ts);
 * the three section containers (Organization Management / Edge Management /
 * Reports) are sourced from Layout.tsx's own
 * `hasPermission(Permission.ViewXManagement, ...)` checks, which gate the
 * whole section independently of its children.
 *
 * A node with no `key` is a purely visual grouping — Reports' General /
 * Faults / Traffic / Car Use / Service Logs subheadings. Layout.tsx never
 * gates one of these on its own permission, only on its children's, so
 * there is nothing here to toggle for the group itself.
 *
 * "Monitoring Dashboard" is a section of its own with NO `key`: Layout.tsx's
 * Monitoring link is gated by the Banks block alone
 * (`SCREEN_PERMISSION.monitoring`), so the section is purely a grouping of
 * eight blocks. Each block has a permission bundle (permissionBundles.ts):
 * enabling it also enables every permission that block's calls need, and the
 * runtime reads the same keys (types/monitoringAccess.ts) — so this tree is
 * the only place an operator has to go.
 */
export interface SidebarTreeNode {
  /** Permission tree_key this row's switch toggles. Absent = pure visual grouping. */
  key?: Permission;
  /** i18n key, when Layout.tsx renders this item's label through t(). */
  labelKey?: string;
  /** Literal label, used when Layout.tsx renders this item as plain text (or as a fallback for labelKey). */
  fallbackLabel: string;
  children?: SidebarTreeNode[];
}

export const SIDEBAR_TREE: SidebarTreeNode[] = [
  {
    labelKey: "sideBar.monitoring",
    fallbackLabel: "Monitoring Dashboard",
    children: [
      {
        // Also the sidebar link's and the /monitoring screen's gate.
        key: Permission.ViewMonitoringDashboard,
        labelKey: "organizationManagement.monitoringBlock.banks",
        fallbackLabel: "Monitoring Dashboard Banks",
      },
      {
        key: Permission.MonitorControlPanelActions,
        labelKey: "organizationManagement.monitoringBlock.actions",
        fallbackLabel: "Monitoring Dashboard Actions",
      },
      {
        key: Permission.MonitorControlPanelFaults,
        labelKey: "organizationManagement.monitoringBlock.faults",
        fallbackLabel: "Monitoring Dashboard Faults",
      },
      {
        key: Permission.MonitorControlPanelStatus,
        labelKey: "organizationManagement.monitoringBlock.status",
        fallbackLabel: "Monitoring Dashboard Status",
      },
      {
        key: Permission.MonitorControlPanelState,
        labelKey: "organizationManagement.monitoringBlock.state",
        fallbackLabel: "Monitoring Dashboard State",
      },
      {
        key: Permission.MonitorControlPanelVipMode,
        labelKey: "organizationManagement.monitoringBlock.vip",
        fallbackLabel: "Monitoring Dashboard VIP",
      },
      {
        key: Permission.ScheduledActionsView,
        labelKey: "organizationManagement.monitoringBlock.scheduledActions",
        fallbackLabel: "Monitoring Dashboard Scheduled Actions",
      },
      {
        key: Permission.MonitorGridCreate,
        labelKey: "organizationManagement.monitoringBlock.grids",
        fallbackLabel: "Monitoring Dashboard Grids",
      },
    ],
  },
  {
    key: Permission.ViewOrganizationManagement,
    labelKey: "sideBar.organizationManagement",
    fallbackLabel: "Organization Management",
    children: [
      {
        key: Permission.ViewOrganizationManagementOrganizations,
        labelKey: "sideBar.organizations",
        fallbackLabel: "Organizations",
      },
      {
        // Shares its permission with Organizations — see screenPermissions.ts
        // (`drafts: Permission.ViewOrganizationManagementOrganizations`),
        // matching the sidebar's own pre-existing value exactly. Its switch
        // always mirrors Organizations' switch for that reason; this is not
        // a bug in this tree.
        key: Permission.ViewOrganizationManagementOrganizations,
        fallbackLabel: "Drafts",
      },
      {
        key: Permission.ViewOrganizationManagementRoles,
        labelKey: "sideBar.roles",
        fallbackLabel: "Roles",
      },
      {
        key: Permission.ViewOrganizationManagementUsers,
        labelKey: "sideBar.users",
        fallbackLabel: "Users",
      },
    ],
  },
  {
    key: Permission.ViewEdgeManagement,
    labelKey: "header.edgeManagement",
    fallbackLabel: "Edge Management",
    children: [
      {
        key: Permission.ViewEdgeManagementAdapters,
        labelKey: "sideBar.adapters",
        fallbackLabel: "Adapters",
      },
      {
        key: Permission.ViewEdgeManagementMap,
        labelKey: "sideBar.mapView",
        fallbackLabel: "Map View",
      },
      {
        key: Permission.ViewEdgeManagementServers,
        labelKey: "sideBar.servers",
        fallbackLabel: "Servers",
      },
    ],
  },
  {
    key: Permission.ViewReportManagement,
    fallbackLabel: "Reports",
    children: [
      {
        fallbackLabel: "General",
        children: [
          {
            key: Permission.ViewReportManagementGeneralCurrentOutOfService,
            labelKey: "general.currentOutOfService",
            fallbackLabel: "Current Out of Service",
          },
          {
            key: Permission.ViewReportManagementGeneralEscalatorInUse,
            labelKey: "general.escalatorInUse",
            fallbackLabel: "Escalator In Use",
          },
          {
            key: Permission.ViewReportManagementGeneralPerformance,
            labelKey: "general.performance",
            fallbackLabel: "Performance",
          },
          {
            key: Permission.ViewReportManagementGeneralBeneficialUsage,
            labelKey: "general.beneficialUsage",
            fallbackLabel: "Beneficial Usage",
          },
          {
            key: Permission.ViewReportManagementGeneralFireService,
            labelKey: "general.fireService",
            fallbackLabel: "Fire Service",
          },
        ],
      },
      {
        fallbackLabel: "Faults",
        children: [
          {
            key: Permission.ViewReportManagementFaultsRecentFaults,
            labelKey: "general.recentFaults",
            fallbackLabel: "Recent Faults",
          },
          {
            key: Permission.ViewReportManagementFaultsRecentFaultsEscmov,
            labelKey: "general.recentFaultsEscMov",
            fallbackLabel: "Recent Faults (EscMov)",
          },
          {
            key: Permission.ViewReportManagementFaultsLogByBank,
            labelKey: "general.faultLogByBank",
            fallbackLabel: "Fault Log By Bank",
          },
          {
            key: Permission.ViewReportManagementFaultsFrequencyByCar,
            labelKey: "general.frequencyByCar",
            fallbackLabel: "Frequency By Car",
          },
          {
            key: Permission.ViewReportManagementFaultsFrequencyByFault,
            labelKey: "general.frequencyByFault",
            fallbackLabel: "Frequency By Fault",
          },
          {
            key: Permission.ViewReportManagementFaultsBycar,
            labelKey: "general.faultByLift",
            fallbackLabel: "Fault By Lift",
          },
          {
            key: Permission.ViewReportManagementFaultsByCarEscmov,
            labelKey: "general.faultByLiftEscMov",
            fallbackLabel: "Fault By Lift (EscMov)",
          },
          {
            key: Permission.ViewReportManagementFaultsByFloor,
            labelKey: "general.faultByFloor",
            fallbackLabel: "Fault By Floor",
          },
          {
            key: Permission.ViewReportManagementFaultsByDayWeek,
            labelKey: "general.faultByDay",
            fallbackLabel: "Fault By Day",
          },
          {
            key: Permission.ViewReportManagementFaultsByDayWeekEscmov,
            labelKey: "general.faultByDayEscMov",
            fallbackLabel: "Fault By Day (EscMov)",
          },
          {
            key: Permission.ViewReportManagementFaultsByType,
            labelKey: "general.faultByType",
            fallbackLabel: "Fault By Type",
          },
          {
            key: Permission.ViewReportManagementFaultsByTypeEscmov,
            labelKey: "general.faultByTypeEscMov",
            fallbackLabel: "Fault By Type (EscMov)",
          },
        ],
      },
      {
        fallbackLabel: "Traffic",
        children: [
          {
            key: Permission.ViewReportManagementTrafficDetailedSummaryReport,
            labelKey: "general.detailedSummaryReport",
            fallbackLabel: "Detailed Summary Report",
          },
          {
            key: Permission.ViewReportManagementTrafficHallCallSummaryReport,
            labelKey: "general.hallCallSummaryReport",
            fallbackLabel: "Hall Call Summary Report",
          },
          {
            key: Permission.ViewReportManagementTrafficLongestWaitTimesList,
            labelKey: "general.longestWaitTimes",
            fallbackLabel: "Longest Wait Times",
          },
          {
            key: Permission.ViewReportManagementTrafficTripsToAndFromFloor,
            labelKey: "general.tripsToAndFromFloor",
            fallbackLabel: "Trips To And From Floor",
          },
          {
            key: Permission.ViewReportManagementTrafficHallButtonCountsByFloor,
            labelKey: "general.byFloor",
            fallbackLabel: "By Floor",
          },
          {
            key: Permission.ViewReportManagementTrafficHallButtonCountByTimeOfDay,
            labelKey: "general.byTimeOfDay",
            fallbackLabel: "By Time Of Day",
          },
          {
            key: Permission.ViewReportManagementTrafficAverageByFloor,
            labelKey: "general.averageByFloor",
            fallbackLabel: "Average By Floor",
          },
          {
            key: Permission.ViewReportManagementTrafficAverageByTimeOfDay,
            labelKey: "general.averageByTimeOfDay",
            fallbackLabel: "Average By Time Of Day",
          },
          {
            key: Permission.ViewReportManagementTrafficDistributionByHourlyInterval,
            labelKey: "general.distributionByHourlyInterval",
            fallbackLabel: "Distribution By Hourly Interval",
          },
          {
            key: Permission.ViewReportManagementTrafficDistributionDetailedTotal,
            labelKey: "general.distributionDetailedTotal",
            fallbackLabel: "Distribution Detailed Total",
          },
          {
            key: Permission.ViewReportManagementTrafficDistributionDetaileDirection,
            labelKey: "general.distributionDetailedDirection",
            fallbackLabel: "Distribution Detailed Direction",
          },
        ],
      },
      {
        fallbackLabel: "Car Use",
        children: [
          {
            key: Permission.ViewReportManagementCarUseByHour,
            labelKey: "general.carUseByHour",
            fallbackLabel: "Car Use By Hour",
          },
          {
            key: Permission.ViewReportManagementCarUseComparisons,
            labelKey: "general.carUseComparisons",
            fallbackLabel: "Car Use Comparisons",
          },
          {
            key: Permission.ViewReportManagementCarUseDoorTimes,
            labelKey: "general.carUseTimes",
            fallbackLabel: "Car Use Times",
          },
        ],
      },
      {
        fallbackLabel: "Service Logs",
        children: [
          {
            key: Permission.ViewReportManagementCarServiceServiceLogTable,
            labelKey: "general.serviceLog",
            fallbackLabel: "Service Log",
          },
          {
            key: Permission.ViewReportManagementCarServiceLog,
            labelKey: "general.log",
            fallbackLabel: "Log",
          },
          {
            key: Permission.ViewReportManagementCarServiceStatistics,
            labelKey: "general.statistics",
            fallbackLabel: "Statistics",
          },
        ],
      },
    ],
  },
];

export default SIDEBAR_TREE;
